import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import Button from "../../../shared/components/Button";
import { Flex } from "../../../shared/components/Flex";
import type { Camera, CameraStatus } from "../model";
import "./CameraCard.scss";

function Icon({
  children,
  className = "icon",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      {children}
    </svg>
  );
}

function CameraIcon() {
  return (
    <Icon>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 8.5A2.5 2.5 0 0 1 6.5 6h2l1.2-1.5h4.6L15.5 6h2A2.5 2.5 0 0 1 20 8.5v7a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 15.5z"
      />
      <circle cx="12" cy="12" r="3.25" />
    </Icon>
  );
}

function StatusPill({ status }: { status: CameraStatus }) {
  const styles: Record<CameraStatus, string> = {
    Live: "status--live",
    Warning: "status--warning",
    Offline: "status--offline",
  };

  return (
    <span className={`status ${styles[status]}`}>
      <span className="status-dot" />
      {status}
    </span>
  );
}

type CameraCardProps = {
  camera: Camera;
  onRemove?: (camera: Camera) => void;
  onSettings?: (camera: Camera) => void;
  onExpand?: (camera: Camera) => void;
};

type StreamState = "waiting" | "connected" | "error";

export function CameraCard({
  camera,
  onRemove,
  onSettings,
  onExpand,
}: CameraCardProps) {
  const isOffline = camera.status === "Offline";
  const [streamState, setStreamState] = useState<StreamState>(
    isOffline ? "error" : "waiting",
  );
  const [lastFrameAt, setLastFrameAt] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    if (!menuPosition) return;
    const closeMenu = () => setMenuPosition(null);
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, [menuPosition]);

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuPosition({
      x: Math.min(event.clientX, window.innerWidth - 145),
      y: Math.min(event.clientY, window.innerHeight - 90),
    });
  };

  return (
    <article className="camera-card" onContextMenu={openMenu}>
      <div className="media" style={{ backgroundColor: camera.accent }}>
        {camera.streamURL && (
          <img
            alt={`Live feed from ${camera.location}`}
            className="feed"
            src={camera.streamURL}
            onError={() => setStreamState("error")}
            onLoad={() => {
              setStreamState("connected");
              setLastFrameAt(Date.now());
            }}
          />
        )}
        <div className={`overlay ${isOffline ? "offline" : ""}`} />
        <div className="grid-overlay" />
        <div className="toolbar">
          <StatusPill status={camera.status} />
        </div>
        {isOffline ? (
          <div className="offline">
            <div>
              <div className="offline-icon">
                <CameraIcon />
              </div>
              <p>Camera offline</p>
            </div>
          </div>
        ) : (
          <div className="live-label">
            <span className="live-label-dot" />
            Live preview
          </div>
        )}
        <Button
          aria-label={`Open ${camera.location}`}
          className="action action--expand"
          onClick={() => onExpand?.(camera)}
          title={`Open ${camera.location} fullscreen`}
        >
          <ExpandIcon />
        </Button>
      </div>
      <div className="footer">
        <div>
          <p className="location">{camera.location}</p>
          <p className="meta">
            {camera.id} · {camera.protocol ? `${camera.protocol.toUpperCase()} feed` : `Last seen ${camera.lastSeen}`}
          </p>
          <Flex className="health-meta" wrap="wrap">
            <span className={`health-state health-state--${streamState}`}>
              <span className="health-state-dot" />
              {streamState === "connected"
                ? "Stream connected"
                : streamState === "error"
                  ? "Stream unavailable"
                  : "Connecting"}
            </span>
            <span>
              {lastFrameAt
                ? `Frame ${new Date(lastFrameAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : `Last seen ${camera.lastSeen}`}
            </span>
          </Flex>
        </div>
        <span className="resolution">1080p</span>
      </div>
      {menuPosition && (
        <div
          aria-label={`Actions for ${camera.location}`}
          className="menu"
          onClick={(event) => event.stopPropagation()}
          role="menu"
          style={{ left: menuPosition.x, top: menuPosition.y }}
        >
          <Button
            onClick={() => {
              setMenuPosition(null);
              onSettings?.(camera);
            }}
            role="menuitem"
          >
            Settings
          </Button>
          <Button
            className="menu-item--danger"
            onClick={() => {
              setMenuPosition(null);
              onRemove?.(camera);
            }}
            role="menuitem"
          >
            Remove
          </Button>
        </div>
      )}
    </article>
  );
}

function ExpandIcon() {
  return (
    <Icon>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 4H4v4M16 4h4v4M20 16v4h-4M4 16v4h4"
      />
    </Icon>
  );
}
